import React from "react";
import { Activity, ActivityParticipantData, ActivityParticipantRole, fieldValidation, rng } from "../../../amorg-common";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { Card } from "../../../designSystem/Card";
import { CloseModalButton } from "../../../designSystem/compositeComponents/CloseModalButton";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Tabber } from "../../../designSystem/Tabber";
import { ParticipantSelector } from "./ParticipantSelector";
import { updateActivityParticipants } from "../../../api/activityClientApi";
import { Button } from "../../../designSystem/button/Button";
import { TextField } from "../../../designSystem/formField/text/TextField";
import { ErrorMessage } from "../../../designSystem/Message";

type ParticipantEditorProps = 
{
    activity:Activity,
    defaultAddRole:ActivityParticipantRole,
    onParticipantsUpdated: () => void,
    onCancel: () => void
}
export function ParticipantEditor(props:ParticipantEditorProps):JSX.Element
{
    const [selectedTab, setSelectedTab] = React.useState<'internals' | 'externals'>('internals');

    return (
        <Flex flexDirection="column" gap={4} style={{minHeight:'700px'}}>

            <Tabber variant='default' 
                items={[
                        {tabName:'internals', text:'Deltagere'},
                        {tabName:'externals', text:'Tilføj ekstern'}
                ]}
                selectedTabName={selectedTab}
                onClick={() => {
                    if(selectedTab === 'internals') {
                        setSelectedTab('externals');
                    } else {
                        setSelectedTab('internals');
                    }
                }}/>

            {selectedTab === 'externals' &&
                <Card variant="outlined">
                    <AddExternalParticipantPanel 
                        activityId={props.activity.id}
                        participants={props.activity.participants}
                        role={props.defaultAddRole}
                        onAdded={()=>{
                            props.onParticipantsUpdated();
                            setSelectedTab('internals');
                        }} 
                        onCancel={()=>{setSelectedTab('internals');}} /> 
                </Card>
            }

            {selectedTab === 'internals' &&
                <Flex flexDirection="column" gap={4}>
                    
                    <Card variant="outlined">

                        <ParticipantSelector
                            activity={props.activity}
                            defaultAddRole={props.defaultAddRole}
                            onParticipantsUpdated={props.onParticipantsUpdated} />

                        <ButtonRow variant="right-aligned">
                            <CloseModalButton onClose={props.onCancel} />
                        </ButtonRow>
                    </Card>

                    
                </Flex>
            }
        </Flex>
    );
}

type AddExternalParticipantPanelProps = { 
    activityId: string,
    participants:ActivityParticipantData[],
    role: ActivityParticipantRole,
    onAdded: ()=>void,
    onCancel:()=>void 
}
function AddExternalParticipantPanel(props: AddExternalParticipantPanelProps): JSX.Element {
    const [submitState, setSubmitState] = React.useState<'pending'|'success'|'error'>('pending');
    const [inputState, setInputState] = React.useState({        
        name:'', 
        role: '', 
        email: '' 
    });
    async function handleClickAdd() {
        const result = await updateActivityParticipants(
            props.activityId,
            [
                ...props.participants,
                {
                    id: rng.getRandomId(),
                    type: { value: 'external' },
                    activityRole: props.role,
                    name: inputState.name,
                    role: inputState.role,
                    email: inputState.email,
                    initials: ''
                }
            ]
        );
        if (result.errorMessage)
            setSubmitState('error');
        else {
            setSubmitState('success');
            props.onAdded();
        }
    }

    return (
        <div>
            <Flex flexDirection="column" gap={5}>
                <ErrorMessage show={submitState==='error'}>
                    En fejl opstod under oprettelsen. Prøv igen.
                </ErrorMessage>

                <Flex flexDirection="column" gap={16}>
                    <TextField
                        label="Navn"
                        value={inputState.name}
                        onChange={(newValue) => {
                            setInputState({ ...inputState, name: newValue })
                        }}
                        validator={(currentValue) => {
                            if((submitState==='pending' && currentValue === '') ||
                                fieldValidation.externalActivityParticipant.name(currentValue))
                                return { valid:true };
                            return { valid:false, errorMessage:`Ugyldigt navn. Skal udfyldes med maks. 100 (${currentValue.length}) tegn` };
                        }} />

                    <TextField
                        label="Email"
                        value={inputState.email}
                        onChange={(newValue) => {
                            setInputState({ ...inputState, email:newValue })
                        }}
                        validator={(currentValue) => {
                            if((submitState==='pending' && currentValue === '') ||
                                fieldValidation.externalActivityParticipant.email(currentValue))
                                return { valid:true };
                            return { valid:false, errorMessage:`Ugyldig e-mail addresse.` };
                        }} />

                    <TextField
                        label="Rolle"
                        value={inputState.role}
                        onChange={(newValue) => {
                            setInputState({ ...inputState, role:newValue })
                        }}
                        validator={(currentValue) => {
                            if((submitState==='pending' && currentValue === '') ||
                                fieldValidation.externalActivityParticipant.role(currentValue))
                                return { valid:true };
                            return { valid:false, errorMessage:`Ugyldig rolle. Skal udfyldes med maks. 100 (${currentValue.length}) tegn` };
                        }} />
                </Flex>
                <ButtonRow variant="right-aligned">
                    <Button
                        variant="text"
                        onClick={() => props.onCancel()}
                        text={"Annuller"} />

                    <Button
                        variant="filled"
                        onClick={handleClickAdd}
                        text={"Opret"} />
                </ButtonRow>
            </Flex>
        </div>
    );
}